import {
  faBezierCurve,
  faFileContract,
  faMoneyCheckDollar
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  RowCustomAction,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  Avatar,
  CardActions,
  CardContent,
  CardHeader,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from '@material-ui/core'
import {
  Add,
  Autorenew,
  Ballot,
  Edit,
  HighlightOff,
  InsertDriveFile,
  SettingsInputComponent,
  ShowChart,
  Tune
} from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumCommodity, EnumEstadoCliente } from '../../../../../api'
import ButtonWithPermissions from '../../../../../components/buttonWithPermissions/buttonWithPermissions'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { ButtonOption, SplitButton } from '../../../../../components/shared/splitButton/splitButton'
import {
  enumEnumToValueNumber,
  enumEnumToValueString,
  enumValueToKey,
  generateUniqueKeyWithNoPrefix,
  toLocaleDate
} from '../../../../../helpers'
import {
  getUserPermissions,
  hasPermission,
  hasResource
} from '../../../../../helpers/permissionsHelper'
import { NonAuthRoutes } from '../../../../../model'
import { Pages, Resources } from '../../../../../model/RBAC/constants'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { ContractModel } from '../../../../../api/Argentia/model/contracts'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog
} from '../../../../../hooks'
import { useShowGoToProcedures } from '../../../../../hooks/useShowGoToProceduresDialog'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { clearActiveFlexibility, storeActiveContract } from '../../../../../redux'
import { selectClientData } from '../../../../../redux/selectors/client.selectors'
import {
  patchContract,
  terminateContract,
  completeDataTableItemActions
} from '../../../../contracts/contracts.service'
import { EnumDocumentsNavigationFrom } from '../../../../documents/documents.model'
import { ProcedureFormMode } from '../../../../procedures/procedures.types'
import {
  isSubjectCommercial,
  isSubjectCommercialOrWholesaler,
  isSubjectExtendedClient,
  isSubjectFinalClient,
  isSubjectWholesaler
} from '../../../client.helper'
import { EmptyContent } from '../shared/emptyCard'
import { styles } from './contractsCard.styles'
import { ContractAddButtonOptions, ContractsCardProps } from './contractsCard.types'

export const ContractsCard: React.FC<ContractsCardProps> = ({
  isFetching,
  isFetchingClient,
  contracts,
  commodities,
  subjectType,
  error,
  onClickAdd,
  onClickEdit,
  onRetry,
  onRefreshProcedureCard
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'enums'])
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const hasPermissionClientOrExtendedEdit =
    hasPermission(Resources.Client, [UserPermission.EDIT]) ||
    hasPermission(Resources.ExtendedClients, [UserPermission.EDIT])

  const clientData = useSelector(selectClientData)

  const { info, error: showError } = useNotify()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { getShowGoToProceduresDialog } = useShowGoToProcedures()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [terminateMenuAnchorEl, setTerminateMenuAnchorEl] = React.useState(null)
  const [openTerminateMenu, setOpenTerminateMenu] = React.useState<boolean>(false)
  const [terminateMenuOptions, setTerminateMenuOptions] = React.useState<any[]>([])

  const getAddButtonOptions = React.useCallback((): ButtonOption[] => {
    let defaultOptions: ButtonOption[] = []

    if (!isSubjectExtendedClient(subjectType)) {
      defaultOptions.push({
        id: 0,
        name: ContractAddButtonOptions.DEFAULT,
        label: t('clientFile:components.contractsCard.actions.add')
      })
    }

    if (isSubjectCommercial(subjectType)) {
      defaultOptions.push({
        id: 1,
        name: ContractAddButtonOptions.COMMERCIAL,
        label: t(`clientFile:components.contractsCard.actions.addCommercial`)
      })
    } else if (commodities && commodities.length > 0) {
      const commoditiesOptions = commodities.flatMap(c => {
        const commodity = enumEnumToValueString(EnumCommodity, c)
        const commodityID = enumEnumToValueNumber(EnumCommodity, c)
        return commodity && commodityID
          ? {
              id: commodityID,
              name: commodity as ContractAddButtonOptions,
              label: t(`clientFile:components.contractsCard.actions.add${c}`)
            }
          : []
      })
      defaultOptions.push(...commoditiesOptions)
    }

    const changeOwnerOption = {
      id: 3,
      name: ContractAddButtonOptions.CHANGE_OWNER as ContractAddButtonOptions,
      label: t(`clientFile:components.contractsCard.actions.changeOwner`)
    }

    if (!isSubjectCommercialOrWholesaler(subjectType) && !isSubjectExtendedClient(subjectType)) {
      defaultOptions.push(changeOwnerOption)
    }

    return defaultOptions
  }, [t, commodities, subjectType])

  const ContractsHeader = () =>
    React.useMemo(() => {
      return (
        <CardHeader
          className={classes.cardheader}
          avatar={
            <Avatar>
              <FontAwesomeIcon size={'lg'} icon={faFileContract as IconProp} />
            </Avatar>
          }
          title={
            <Typography className={classes.cardTitle} color={'secondary'}>
              {t('clientFile:components.contractsCard.title')}
            </Typography>
          }
        />

        // <Header
        //   avatarIcon={<FontAwesomeIcon size={'lg'} icon={faFileContract as IconProp} />}
        //   title={t('clientFile:components.contractsCard.title')}
        //   titleVariant="secondary"
        // />
      )
    }, [t])

  const columns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'commodity',
      label: t('clientFile:components.contractsCard.dataTable.columns.commodity'),
      type: DataType.TEXT
    },
    {
      name: 'contractCode',
      label: t('clientFile:components.contractsCard.dataTable.columns.contractCode'),
      type: DataType.TEXT
    },
    {
      name: 'cupsListFormatted',
      label: t('clientFile:components.contractsCard.dataTable.columns.cups'),
      type: DataType.LONGTEXT,
      columnWidth: 200,
      display: isSubjectFinalClient(subjectType) || isSubjectExtendedClient(clientData?.type)
    },
    {
      name: 'startDate',
      label: t('clientFile:components.contractsCard.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('clientFile:components.contractsCard.dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'status',
      label: t('clientFile:components.contractsCard.dataTable.columns.status'),
      type: DataType.TEXT
    }
  ]

  const handleViewContract = (contractID: number) => {
    !isFetchingClient && history.push(NonAuthRoutes.ContractEdit, { contractID: contractID })
  }

  const handleDocumentAdd = (contract: ContractModel) => {
    !isFetchingClient &&
      history.push(NonAuthRoutes.Documents, {
        elementId: contract.id,
        elementCode: contract.contractCode,
        commodity: contract.commodity,
        navigation: EnumDocumentsNavigationFrom.Contracts
      })
  }

  const handleViewDetails = (contract: ContractModel) => {
    !isFetchingClient &&
      history.push(NonAuthRoutes.ContractDetails, { contractID: contract.id, contract })
  }

  const handleViewAdvancedDetails = (contract: any) => {
    !isFetchingClient &&
      history.push(NonAuthRoutes.CupsDetails, { CUPSCode: contract.cupsListFormatted })
  }

  const handleViewQuantities = (contract: ContractModel) => {
    storeActiveContract(contract)
    !isFetchingClient &&
      history.push(NonAuthRoutes.ContractQuantities, { contractID: contract.id, contract })
  }

  const onCloseEditForm = async (contract: ContractModel, date?: Date) => {
    if (date) {
      await terminateContract(contract.id, date).then(response => {
        if (response.success) {
          info(
            t('clientFile:components.contractsCard.components.terminateDialog.success', {
              contractCode: contract.contractCode,
              terminationDate: toLocaleDate(moment(date).toDate())
            })
          )
          onRetry()
        } else {
          showError(t('clientFile:components.contractsCard.components.terminateDialog.error'))
        }
      })
    }

    closeForm()
  }

  const onPatchContract = async (contract: ContractModel, status: EnumEstadoCliente) => {
    const request: ContractModel = {
      ...contract,
      status
    }
    await patchContract(request).then(response => {
      if (response.success) {
        info(t('clientFile:components.contractsCard.notifications.success.patch'))
        onRetry()
      } else {
        showError(t('clientFile:components.contractsCard.notifications.error.patch'))
      }
    })
  }

  const terminateDialogDefinition = (contract: ContractModel): FormDialogDefinitionProps => {
    return {
      id: 'EDIT',
      sections: [
        {
          inputs: [
            {
              name: 'terminationDate',
              label: t(
                'clientFile:components.contractsCard.components.terminateDialog.inputs.terminationDate'
              ),
              helpText: t(
                'clientFile:components.contractsCard.components.terminateDialog.helpText',
                {
                  startDate: contract.startDate
                    ? toLocaleDate(moment(contract.startDate).toDate())
                    : '',
                  endDate: contract.endDate ? toLocaleDate(moment(contract.endDate).toDate()) : ''
                }
              ),
              errorText: t(
                'clientFile:components.contractsCard.components.terminateDialog.helpText',
                {
                  startDate: contract.startDate
                    ? toLocaleDate(moment(contract.startDate).toDate())
                    : '',
                  endDate: contract.endDate ? toLocaleDate(moment(contract.endDate).toDate()) : ''
                }
              ),
              type: DataType.DATE,
              minDate: contract.startDate,
              maxDate: contract.endDate,
              required: true
              // errorText: endDateTractMsgError ? getErrorText(endDateTractMsgError) : '',
            }
          ]
        }
      ]
    }
  }

  const showTerminateContractDialog = async (contract: ContractModel) => {
    openForm({
      definition: terminateDialogDefinition(contract),
      title: t('clientFile:components.contractsCard.components.terminateDialog.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        onCloseEditForm(contract, formData?.terminationDate)
      },
      inputData: {
        terminationDate: contract.endDate
          ? moment().isBetween(contract.startDate, contract.endDate)
            ? moment().toString()
            : contract.endDate
          : moment().isAfter(contract.startDate)
            ? moment().toString()
            : contract.startDate
      }
    })
  }
  // ------------------------------------------ //

  const handleActivateContract = (row: ContractModel) => {
    history.push(NonAuthRoutes.ProceduresAdd, {
      contractId: row.id,
      formMode:
        row.commodity === EnumCommodity.Electricidad
          ? ProcedureFormMode.ALTA_CONTRATO_POWER
          : ProcedureFormMode.ALTA_CONTRATO_GAS
    })
  }

  const handleTerminateContract = async (contract: ContractModel) => {
    if (hasResource(Resources.ComunicationsCenterProcedures)) {
      getShowGoToProceduresDialog({
        contractID: contract.id,
        origin:
          enumEnumToValueNumber(EnumCommodity, contract.commodity) === EnumCommodity.Electricidad
            ? ProcedureFormMode.BAJA_CONTRATO_POWER
            : ProcedureFormMode.BAJA_CONTRATO_GAS,
        type: ConfirmationDialogs.GO_TO_PROCEDURES_TERMINATION,
        cancelAction: () => showTerminateContractDialog(contract)
      })
    } else {
      showTerminateContractDialog(contract)
    }
    onMenuOptionsCloseHandler()
  }

  const handleSuspendContract = async (contract: ContractModel) => {
    if (hasResource(Resources.ComunicationsCenterProcedures)) {
      getShowGoToProceduresDialog({
        contractID: contract.id,
        origin:
          enumEnumToValueNumber(EnumCommodity, contract.commodity) === EnumCommodity.Electricidad
            ? ProcedureFormMode.SUSPENSION_CONTRATO_POWER
            : ProcedureFormMode.SUSPENSION_CONTRATO_GAS,
        type: ConfirmationDialogs.GO_TO_PROCEDURES_SUSPEND,
        cancelAction: () => onPatchContract(contract, EnumEstadoCliente.Cortado)
      })
    } else {
      onPatchContract(contract, EnumEstadoCliente.Cortado)
    }
    onMenuOptionsCloseHandler()
  }

  const handleViewFlexibilities = async (contract: ContractModel) => {
    storeActiveContract({
      ...contract,
      commodity: enumEnumToValueNumber(EnumCommodity, contract.commodity)
    } as ContractModel)
    clearActiveFlexibility()
    history.push(NonAuthRoutes.ContractFlexibilities, { contractID: contract.id })
  }

  const handleViewPriceComponents = async (contract: ContractModel) => {
    storeActiveContract({
      ...contract,
      commodity: enumEnumToValueNumber(EnumCommodity, contract.commodity)
    } as ContractModel)
    clearActiveFlexibility()
    history.push(NonAuthRoutes.ContractPriceComponents, { contractID: contract.id })
  }

  const handleViewOrders = async (contract: ContractModel) => {
    storeActiveContract({
      ...contract,
      commodity: enumEnumToValueNumber(EnumCommodity, contract.commodity)
    } as ContractModel)
    history.push(NonAuthRoutes.ContractOrders, { contractID: contract.id })
  }

  // ---------------- Menu Options Handlers ------------------ //

  const handleTerminateMenuOptions = (row: ContractModel, event: any) => {
    let menuOptions: any[] = [
      {
        label: t('clientFile:components.contractsCard.menuOptions.terminate'),
        onClick: () => handleTerminateContract(row)
      },
      {
        label: t('clientFile:components.contractsCard.menuOptions.suspend'),
        onClick: () => handleSuspendContract(row)
      }
    ]
    setTerminateMenuOptions(menuOptions)
    setTerminateMenuAnchorEl(event.currentTarget)
    setOpenTerminateMenu(true)
  }

  const onMenuOptionsCloseHandler = () => {
    setTerminateMenuAnchorEl(null)
    setOpenTerminateMenu(false)
  }

  // ---------------- Menu Options Handlers ------------------ //

  const getRowCustomActions = () => {
    const baseCustomActions: RowCustomAction[] = [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('clientFile:components.contractsCard.dataTable.actions.edit'),
        onClick: (row: ContractModel) => handleViewContract(row.id!)
      },
      {
        icon: <HighlightOff fontSize="small" />,
        tooltip: t('clientFile:components.contractsCard.dataTable.actions.terminate'),
        onClick: handleTerminateMenuOptions,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canTerminate'
      },
      {
        icon: <Add fontSize="small" />,
        tooltip: t('clientFile:components.contractsCard.dataTable.actions.activate'),
        onClick: handleActivateContract,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canActivate'
      },
      {
        icon: <InsertDriveFile fontSize="small" />,
        tooltip: t('clientFile:components.contractsCard.dataTable.actions.documents'),
        onClick: (row: ContractModel) => handleDocumentAdd(row),
        userPermission: UserPermission.EDIT
      }
    ]

    let customActions = baseCustomActions

    if (isSubjectFinalClient(clientData?.type) || isSubjectExtendedClient(clientData?.type)) {
      customActions.push(
        {
          icon: <SettingsInputComponent fontSize="small" />,
          tooltip: t('clientFile:components.contractsCard.dataTable.actions.editDetails'),
          onClick: (row: ContractModel) => handleViewDetails(row),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Tune fontSize="small" />,
          tooltip: t('clientFile:components.contractsCard.dataTable.actions.advancedEdition'),
          onClick: (row: ContractModel) => handleViewAdvancedDetails(row),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <ShowChart fontSize="small" />,
          tooltip: t('clientFile:components.contractsCard.dataTable.actions.editQuantities'),
          onClick: (row: ContractModel) => handleViewQuantities(row),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faBezierCurve as IconProp} />,
          tooltip: t('clientFile:components.contractsCard.dataTable.actions.viewFlexibilities'),
          onClick: (row: ContractModel) => handleViewFlexibilities(row),
          userPermission: UserPermission.EDIT,
          visibleIf: 'hasFlexibilities'
        },
        {
          icon: <Ballot fontSize="small" />,
          tooltip: t('clientFile:components.contractsCard.dataTable.actions.viewContractOrders'),
          onClick: (row: ContractModel) => handleViewOrders(row),
          userPermission: UserPermission.EDIT
        }
      )
    }
    if (
      isSubjectFinalClient(clientData?.type) ||
      isSubjectExtendedClient(clientData?.type) ||
      isSubjectWholesaler(clientData?.type)
    ) {
      customActions.push({
        icon: <FontAwesomeIcon size="sm" icon={faMoneyCheckDollar as IconProp} />,
        tooltip: t('clientFile:components.contractsCard.dataTable.actions.viewPriceComponents'),
        onClick: (row: ContractModel) => handleViewPriceComponents(row),
        userPermission: UserPermission.EDIT
      })
    }

    return customActions
  }

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    filterType: FilterType.ADVANCED,
    search: true,
    download: false,
    print: false,
    selectableRows: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 20],
    rowCustomActions: getRowCustomActions()
  }

  const handleClickAdd = (option: ButtonOption) => {
    onClickAdd(option)
  }

  if (isFetching) {
    return <LoadingCard />
  }

  if (error) {
    return (
      <CardWithTransition show={true} raised>
        <ContractsHeader />
        <EmptyContent title={t('clientFile:components.contractsCard.errorCard.errorMessage')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onRetry}
          >
            {t('clientFile:components.contactsCard.errorCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  return (
    <>
      <CardWithTransition show={true} raised>
        {!error && contracts && contracts.length > 0 ? (
          <>
            <CardContent className={classes.cardContent}>
              <DataTable
                title={<ContractsHeader />}
                className={classes.dataTableWithoutHeader}
                data={contracts?.map(contract => completeDataTableItemActions(contract))}
                columns={columns}
                options={tableOptions}
                userPermissions={getUserPermissions(Pages.Client)}
              />
              <Menu
                keepMounted={false}
                elevation={2}
                anchorEl={terminateMenuAnchorEl}
                open={openTerminateMenu}
                onClose={onMenuOptionsCloseHandler}
              >
                {terminateMenuOptions.map((downloadOption, index) => (
                  <MenuItem dense key={index} onClick={downloadOption.onClick}>
                    <Typography variant="inherit">{downloadOption.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </CardContent>
            <CardActions className={classes.cardActionsRight}>
              <ButtonWithPermissions
                size="small"
                color="primary"
                disabled={isFetching || isFetchingClient}
                // Ocultar para Cliente Extendido ya que lleva al front MVC
                hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
                onClick={onClickEdit}
              >
                {t('clientFile:components.contractsCard.actions.edit')}
              </ButtonWithPermissions>
              <SplitButton
                options={getAddButtonOptions()}
                onClick={handleClickAdd}
                disabled={isFetching || isFetchingClient}
                hasPermission={hasPermissionClientOrExtendedEdit}
              />
            </CardActions>
          </>
        ) : (
          <EmptyContent title={t('clientFile:components.contractsCard.emptyCard.title')}>
            <SplitButton
              options={getAddButtonOptions()}
              onClick={handleClickAdd}
              hasPermission={hasPermissionClientOrExtendedEdit}
            />
          </EmptyContent>
        )}
      </CardWithTransition>
      <CustomFormDialog maxWidth={'xs'} {...formProps}></CustomFormDialog>
    </>
  )
}
